<template>
  <NuxtLoadingIndicator :height="5" />
  <div class="app relative min-h-screen">
    <Toaster v-if="toasts" :toasts="toasts" @close="removeToast" />

    <CartCollisionModal
      v-if="
        $features?.cart?.is_enabled && $features.cart.configs?.conflict_handling
      "
      v-model:is-open="isCartCollisionModalOpen"
      @claim="handleClaimCart"
      @merge="handleMergeCart"
      @close="handleRestoreCart"
    />

    <NuxtLayout>
      <NuxtPage class="h-full w-full" />
    </NuxtLayout>
  </div>
</template>

<script setup lang="ts">
import { polyfillCountryFlagEmojis } from "country-flag-emoji-polyfill";
import { useOnline } from "@vueuse/core";
import useLocale from "~/composables/Common/useLocale";
import useGAnalytics from "~/composables/Common/useGAnalytics";
import useCart from "~/composables/Checkout/useCart";
import useWishlist from "~/composables/ProductManagement/useWishlist";
import useB2bUser from "~/composables/UserManagement/useB2bUser";
import useTenant from "~/composables/Tenant/common/useTenant";
import Toaster from "~/components/common/Atom/Toaster/index.vue";
import CartCollisionModal from "~/components/features/Checkout/Organism/CartCollisionModal.vue";
import useOrders from "~/composables/UserManagement/useOrders";

const isCartCollisionModalOpen = useState<boolean>("cart-collision-modal");

const isOnline = useOnline();
const { initLocale, currentLocale } = useLocale();
const { getGAnalyticsScript } = useGAnalytics();
const { toasts, removeToast } = useToast();
const { initWishlist } = useWishlist();
const { cart, cartCookie, mergeCart, claimCart, initCart } = useCart();
const { fetchActiveOrders, fetchInActiveOrders } = useOrders();
const { initUser } = useB2bUser();
const { tenant, refresh } = useTenant();

const userToken = useCookie("hs-token");

const nuxtApp = useNuxtApp();

useHead(() => ({
  htmlAttrs: {
    lang: currentLocale.value?.code ?? "en",
    dir: currentLocale.value?.dir ?? "ltr",
  },
  title: "Quills",
  titleTemplate: "%s Shop",
  script: getGAnalyticsScript(),
  link: [{ rel: "icon", href: nuxtApp.$appConfig?.favicon_url }],
  meta: [
    {
      name: "description",
      content:
        "Browse our huge collection of products, find great deals, and shop for the perfect items to meet your needs. Enjoy fast shipping and a variety of products.",
    },
    {
      name: "keywords",
      content:
        "online shopping, shop now, best deals, buy online, fast shipping, affordable products, shop page",
    },
  ],
}));

if (nuxtApp.$device.isWindows) {
  polyfillCountryFlagEmojis();
}

const handleClaimCart = () => {
  if (cartCookie.value) claimCart(cartCookie.value);
  isCartCollisionModalOpen.value = false;
};

const handleMergeCart = () => {
  if (cartCookie.value && cart.value) {
    mergeCart(cartCookie.value, cart.value.id);
  }
  isCartCollisionModalOpen.value = false;
};

const handleRestoreCart = () => {
  if (cart.value) {
    cartCookie.value = cart.value.id;
  }
};

initUser(nuxtApp.$baseUrl);

onBeforeMount(() => {
  const initData = () => {
    if (userToken.value) {
      initWishlist(nuxtApp.$baseUrl);
      fetchActiveOrders(nuxtApp.$baseUrl);
      fetchInActiveOrders(nuxtApp.$baseUrl);
    }
  };

  initData();
  initCart(nuxtApp.$baseUrl);

  watch([userToken], initData);
});

onMounted(() => {
  initLocale();

  if (nuxtApp.$preview && tenant.value?.refresh_token) {
    // refresh before the session expires in two hours
    setInterval(() => refresh(), 1000 * 60 * 60 * 1.75);
  }

  watchEffect(() => {
    if (!isOnline.value) {
      throw createError({
        statusCode: 404,
        message: "You are not connected to the internet!",
        fatal: true,
      });
    }
  });
});
</script>

<style>
.nuxt-loading-indicator {
  background: theme("colors.action") !important;
}
</style>
